const rawApiBaseUrl = process.env.REACT_APP_API_URL?.trim() || '';

export const API_BASE_URL = rawApiBaseUrl.replace(/\/+$/, '');

export const ACTIVE_INITIATIVE_KEY = 'spartis.activeInitiativeId';

export function apiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return API_BASE_URL ? `${API_BASE_URL}${normalizedPath}` : normalizedPath;
}

export function getActiveInitiativeId(): string | null {
  try {
    const raw = localStorage.getItem(ACTIVE_INITIATIVE_KEY);
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed === '' ? null : trimmed;
  } catch {
    return null;
  }
}

export function setActiveInitiativeId(id: string | number | null): void {
  try {
    if (id === null || id === undefined || `${id}`.trim() === '') {
      localStorage.removeItem(ACTIVE_INITIATIVE_KEY);
    } else {
      localStorage.setItem(ACTIVE_INITIATIVE_KEY, `${id}`);
    }
    window.dispatchEvent(new CustomEvent('spartis:activeInitiativeChanged'));
  } catch {}
}

// ─── Admin / internal API credential ───────────────────────────────────────
// SECURITY: the admin credential is the staff-entered SYNTERRA_ADMIN_CODE, stored
// here under `spartis_admin_code`. It is intentionally SEPARATE from the public
// research-preview code that AccessGate stores under `spartis_access_v2`. Reusing
// the preview code as X-Admin-Code (the old behavior) let any preview user reach
// internal admin endpoints, so adminFetch must NEVER read the preview grant.
const ADMIN_CODE_KEY = 'spartis_admin_code';

export function getAdminCode(): string {
  try {
    return (localStorage.getItem(ADMIN_CODE_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function setAdminCode(code: string): void {
  try {
    const value = (code || '').trim();
    if (value) {
      localStorage.setItem(ADMIN_CODE_KEY, value);
    } else {
      localStorage.removeItem(ADMIN_CODE_KEY);
    }
    window.dispatchEvent(new CustomEvent('spartis:adminCodeChanged'));
  } catch {}
}

export function clearAdminCode(): void {
  setAdminCode('');
}

export function hasAdminCode(): boolean {
  return getAdminCode() !== '';
}

export function adminHeaders(extra?: Record<string, string>): Record<string, string> {
  const code = getAdminCode();
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...extra };
  // ROLE-BASED ACCESS (#162): only attach the break-glass X-Admin-Code when a
  // staff member has actually entered one. Normal staff access now rides on the
  // session cookie (credentials:'include' below); sending an empty X-Admin-Code
  // header would make the backend treat it as a (blank, invalid) admin attempt
  // instead of falling through to session-based authorization.
  if (code && !headers['X-Admin-Code'] && !headers['x-admin-code']) {
    headers['X-Admin-Code'] = code;
  }
  const initId = getActiveInitiativeId();
  if (initId && !headers['X-Initiative-Id'] && !headers['x-initiative-id']) {
    headers['X-Initiative-Id'] = initId;
  }
  return headers;
}
